import React, { useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { styled, Theme } from "@mui/material/styles"
import { Fade } from "@mui/material"
import Box from "@mui/material/Box"
import Modal from "@mui/material/Modal"
import { FULFILLED, OWNER, PROFILE, UPDATE_AVATAR } from "constants/index"
import { PROFILE_IMAGE_CLICKED } from "constants/events"
import { updateAvatarSchema } from "validations/userProfile"
import { useAppSelector } from "appRedux/hooks"
import { useUpdateAvatarMutation } from "appRedux/slices/userApiSlice"
import { updateShowEnlargedAvatarModal } from "appRedux/slices/globalSlice"
import Avatar from "components/atoms/Avatar"
import ImageInputWithCropperModal from "components/molecules/ImageInputWithCropperModal"
import UpdateAvatarActionButtons from "components/molecules/ProfileBanner/UpdateAvatarActionButtons"
import withRoleBasedPermission from "components/HOCs/withRoleBasedPermission"

type ProfileImageProps = {
  imageSrc: File | string | null;
  isFixed: boolean;
  transitionDuration: number;
}

const FadeWithRoleBasedPermission = withRoleBasedPermission(Fade)

export default function ProfileImage(props: ProfileImageProps) {
  const { imageSrc, isFixed, transitionDuration } = props

  const role = useAppSelector((state) => state.global.role)
  const showEnlargedAvatar = useAppSelector((state) => state.global.showEnlargedAvatar)

  const containerRef = useRef<HTMLDivElement | null>(null)

  const [triggerUpdateAvatar, updateAvatarResult] = useUpdateAvatarMutation()

  const dispatch = useDispatch()

  const {
    register,
    setValue
  } = useForm<UpdateAvatarRequestPayload>({
    resolver: zodResolver(updateAvatarSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: { avatar: new File([], "") }
  })

  useEffect(() => {
    if (updateAvatarResult.status === FULFILLED) {
      dispatch(updateShowEnlargedAvatarModal(false))
    }
  }, [updateAvatarResult])

  async function setItemImage(value: File) {
    setValue("avatar", value, { shouldDirty: true })
    await handleUpdateAvatar(value || null)
  }

  async function handleUpdateAvatar(value: File | null) {
    try {
      const formData = new FormData()
      const parsedValue = await updateAvatarSchema.parseAsync({ avatar: value })
      // @ts-ignore
      formData.append("avatar", parsedValue.avatar)
      triggerUpdateAvatar(formData)
    } catch (err) {
      console.error(err)
    }
  }

  function handleContainerClicked() {
    if (isFixed) return
    if (!imageSrc || (imageSrc instanceof File && imageSrc.size === 0)) return
    toggleShowProfileAvatarModal()
  }

  function toggleShowProfileAvatarModal() {
    document.dispatchEvent(new CustomEvent(PROFILE_IMAGE_CLICKED))
  }

  return (
    <>
      {(role === OWNER && (!imageSrc || (imageSrc instanceof File && imageSrc?.size === 0))) ?
        <AvatarInputWrapper id="avatar-input-wrapper" ref={containerRef}>
          <img id="placeholder" src="/static/images/placeholder/user-placeholder.png" alt="avatar"/>
          <ImageInputWithCropperModal
            id="avatar"
            register={register}
            setValue={setValue}
            containerRef={containerRef}
            isSubmitting={updateAvatarResult.isLoading}
            isSingleForm={true}
            onChange={setItemImage}
          />
        </AvatarInputWrapper> : <Container
          isenlarged={showEnlargedAvatar ? 1 : 0}
          transitionduration={transitionDuration}
          onClick={handleContainerClicked}
        >
          <AvatarWrapper id="avatar-wrapper">
            <Avatar imageSrc={imageSrc}/>
          </AvatarWrapper>
        </Container>}

      <Modal
        id="enlarged-avatar-modal"
        sx={{ backdropFilter: "blur(3px)", zIndex: 99 }}
        open={showEnlargedAvatar}
        onClose={toggleShowProfileAvatarModal}
      >
        <Box>
          <FadeWithRoleBasedPermission
            resource={PROFILE}
            action={UPDATE_AVATAR}
            in={showEnlargedAvatar}
          >
            <Box>
              <UpdateAvatarActionButtons
                loading={updateAvatarResult.isLoading}
                onChange={handleUpdateAvatar}
              />
            </Box>
          </FadeWithRoleBasedPermission>
        </Box>
      </Modal>
    </>
  )
}

type ContainerProps = {
  theme?: Theme;
  isenlarged: 1 | 0;
  transitionduration: number;
}

const Container = styled("div")(({ theme, isenlarged, transitionduration }: ContainerProps) => ({
  width: isenlarged ? "70vw" : "100%",
  height: isenlarged ? "70vw" : "100%",
  maxWidth: "calc(var(--max-container-width) * 0.7)",
  maxHeight: "calc(var(--max-container-width) * 0.7)",
  aspectRatio: 1,
  padding: "4px",
  borderRadius: "50%",
  backgroundColor: "#FFFFFF",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: isenlarged ? "absolute" : "relative",
  top: isenlarged ? "calc(100vh / 2)" : "50%",
  left: "50%",
  transformOrigin: "0 0",
  transform: isenlarged ? "translate(-50%, -50%)" : "translate(-50%, -50%)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  transitionProperty: "width, height, transform, top, right",
  transitionDuration: `${transitionduration}s`,
  transitionTimingFunction: "ease-in-out",
  zIndex: isenlarged ? 100 : 1,

  "@supports (height: 100dvh)": {
    top: isenlarged ? "calc(100dvh / 2)" : "50%"
  },

  "& input": {
    display: "none"
  }
}))

const AvatarInputWrapper = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  flexShrink: 0,
  aspectRatio: 1,
  borderRadius: "50%",
  backgroundColor: theme.palette.gray["5"],
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  position: "relative",

  "& img#placeholder": {
    width: "24px",
    height: "24px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    pointerEvents: "none"
  }
}))

const AvatarWrapper = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  borderRadius: "50%"
}))
